'use client';

import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@prismo-io/design-system';
import { useSearchParams } from 'next/navigation';
import { type FC, type PropsWithChildren, useMemo } from 'react';

type Props = {
  name: string;
  noBg?: boolean;
  needFlex?: boolean;
};

export const FocusElement: FC<PropsWithChildren<Props>> = (props) => {
  const { children, name, noBg = false, needFlex = true } = props;

  const searchParams = useSearchParams();

  const isFocus = useMemo(
    () => searchParams.has('focus') && searchParams.get('focus') === name,
    [searchParams, name]
  );

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  if (isMobile) {
    return <>{children}</>;
  }

  return (
    <>
      <Box
        component="div"
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgcolor={alpha('#000000', 0.2)}
        zIndex={isFocus ? 975 : 0}
        sx={{
          opacity: isFocus ? 1 : 0,
          pointerEvents: 'none',
          transitionProperty: 'all',
          transitionDuration: '500ms',
        }}
      />
      <Box
        component="div"
        position="relative"
        sx={needFlex ? { flex: '1 1 0%' } : { flex: '0 0 0%' }}
        data-focus={name}
        data-isfocus={isFocus}
      >
        <Box
          component="div"
          position="absolute"
          top="-1rem"
          right="-1rem"
          left="-1rem"
          bottom="-1rem"
          bgcolor={(theme: Theme) =>
            noBg ? undefined : theme.palette.background.default
          }
          zIndex={isFocus ? 980 : 0}
          borderRadius={2}
          sx={{
            opacity: isFocus ? 1 : 0,
            pointerEvents: 'none',
            transitionProperty: 'all',
            transitionDuration: '500ms',
          }}
        />
        <Box component="div" position="relative" zIndex={isFocus ? 985 : 0}>
          {children}
        </Box>
      </Box>
    </>
  );
};
